import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PUBLIC_HELP_TUTORIAL } from '../graphql/helpTutorials';

export default function PublicHelpTutorial({ tutorialId, buttonLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);
  const [isFeatureAvailable, setIsFeatureAvailable] = useState(true);

  const { data, loading, error } = useQuery(GET_PUBLIC_HELP_TUTORIAL, {
    variables: { id: tutorialId },
    skip: !isOpen || !isFeatureAvailable, // Only fetch when dialog is opened and feature is available
    errorPolicy: 'all', // Don't crash on errors
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      // Check if this is a backend feature availability issue
      if (error.message.includes('Internal server error') || 
          error.message.includes('Cannot query field "publicHelpTutorial"') ||
          error.graphQLErrors?.some(err => err.message.includes('publicHelpTutorial'))) {
        console.warn('Public help tutorial feature not available on backend:', error.message);
        setIsFeatureAvailable(false);
        setHasCheckedAvailability(true);
      }
    }
  });

  const handleOpen = () => {
    if (!hasCheckedAvailability && isFeatureAvailable) {
      setHasCheckedAvailability(true);
    }
    setIsOpen(true);
  };
  
  const handleClose = () => setIsOpen(false);

  // Don't render the button if the feature is not available on the backend
  if (!isFeatureAvailable) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="inline-flex items-center text-sm text-sky-300 hover:text-sky-200 transition-colors duration-200"
      >
        <svg 
          className="w-5 h-5 mr-1" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
        {buttonLabel}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-sky-950 text-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400"></div>
              </div>
            ) : error ? (
              <div className="text-red-400 p-4">
                <h3 className="font-semibold mb-2">Tutorial Not Available</h3>
                <p className="mb-4">
                  {error.message.includes('Internal server error') 
                    ? 'The tutorial system is currently being updated. Please try again later.'
                    : error.message.includes('not found') || error.message.includes('not accessible')
                    ? 'This tutorial is not available at the moment.'
                    : error.message.includes('publicHelpTutorial')
                    ? 'The tutorial feature is being deployed. Please check back soon.'
                    : 'Unable to load tutorial. Please try again later.'
                  }
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : data?.publicHelpTutorial ? (
              <>
                <h2 className="text-xl font-semibold mb-4">{data.publicHelpTutorial.title}</h2>
                <div 
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: data.publicHelpTutorial.content }}
                />
                {data.publicHelpTutorial.videoUrl && (
                  <div className="mt-4">
                    {data.publicHelpTutorial.videoUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img
                        src={data.publicHelpTutorial.videoUrl}
                        alt="Tutorial media"
                        className="w-full h-auto max-h-60 object-contain"
                      />
                    ) : (
                      <video
                        src={data.publicHelpTutorial.videoUrl}
                        controls
                        className="w-full h-auto max-h-60"
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="text-yellow-400 p-4">
                <h3 className="font-semibold mb-2">Tutorial Not Found</h3>
                <p className="mb-4">The tutorial you're looking for is not available or has been moved.</p>
                <div className="flex justify-end">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
} 